import html2canvas from "html2canvas";
import _, { isArray } from "lodash";
import { RulePrefix } from "constants/TemplateEditor";
import {
  SECTIONS_CASE,
  SECTIONS_CREDITOR,
  SECTIONS_DEBTOR,
  SECTIONS_SUBCASE,
} from "constants/TemplateEditor";
import { TemplateGroups } from "components/TemplateEditor/Components/SectionPicker/Contstants";

/**
 * @description Remove unwanted tokens which start with Sett_
 * @param {any[]} tokens - tokens data array
 * @returns {any[]}
 */
const getCleanedDataTokens = (tokens: any[]): any[] => {
  try {
    return tokens.filter((token) => !token.tokenName.includes("Sett"));
  } catch (error) {
    return tokens;
  }
};

export const getDataTokens = (
  adapters: Array<any>,
  type: "all" | "repeating" | "standards"
) => {
  try {
    let tokens: any[] = [];

    if (type == "repeating") {
      const repeatingAdapters = filterAdapters(adapters, true);
      repeatingAdapters.map((adapter, index) => {
        tokens = _.orderBy(
          [...tokens, ...getCleanedDataTokens(adapter.dataTokens)],
          ["tokenName"],
          ["asc"]
        );
      });
      return tokens;
    } else if (type == "standards") {
      const standardAdapters = filterAdapters(adapters);
      standardAdapters.map((adapter, index) => {
        tokens = _.orderBy(
          [...tokens, ...getCleanedDataTokens(adapter.dataTokens)],
          ["tokenName"],
          ["asc"]
        );
      });
      return tokens;
    } else {
      adapters.map((adapter, index) => {
        tokens = _.orderBy(
          [...tokens, ...getCleanedDataTokens(adapter.dataTokens)],
          ["tokenName"],
          ["asc"]
        );
      });
      return tokens;
    }
  } catch (error) {
    return [];
  }
};

/**
 * @description The function `filterAdapters` takes an array of adapters and a boolean flag indicating whether the
 * adapters are repeating or not, and returns a filtered array of adapters with sorted data tokens.
 * @param {Array<any>} adapters - An array of objects representing adapters.
 * @param {boolean} [isRepeating=false] - The `isRepeating` parameter is a boolean value that
 * determines whether the adapters should be filtered based on whether they are repeating or not. If
 * `isRepeating` is `true`, only the repeating adapters will be included in the filtered result. If
 * `isRepeating` is `false`
 * @returns an array of filtered adapters. Each adapter in the array has its `dataTokens` property
 * sorted in ascending order by `tokenName`.
 */
export const filterAdapters = (
  adapters: Array<any>,
  isRepeating: boolean = false
) => {
  try {
    if (isArray(adapters) && adapters.length > 0) {
      const filteredAdapters: Array<any> = adapters.filter(
        (adapter) => adapter.isRepeating === isRepeating
      );
      return filteredAdapters.map((adapter, index) => {
        return {
          ...adapter,
          dataTokens: _.orderBy(
            getCleanedDataTokens(adapter.dataTokens),
            ["tokenName"],
            ["asc"]
          ),
        };
      });
    } else {
      return [];
    }
  } catch (error) {
    return [];
  }
};

/**
 * @description The function `updateAdapterDataTokens` updates the `adapters` array with the data from the
 * `updatedAdapters` array if it is not empty.
 * @param {Array<any>} adapters - An array of objects representing adapters. Each object should have an "adapterId"
 * property.
 * @param {Array<any>} updatedAdapters - An array of objects representing the updated adapters. Each object should
 * have a property called "adapterId" which is used to identify the adapter to be updated.
 * @returns the updated `adapters` array.
 */
export const updateAdapterDataTokens = (
  adapters: Array<any>,
  updatedAdapters: Array<any>
) => {
  try {
    if (isArray(updatedAdapters) && updatedAdapters.length > 0) {
      const adapterIndex = adapters.findIndex(
        (adapter) => adapter.adapterId == updatedAdapters[0].adapterId
      );
      adapters[adapterIndex] = updatedAdapters[0];
      return adapters;
    } else {
      return adapters;
    }
  } catch (error) {
    return adapters;
  }
};

/**
 * @description Generate preview image of a template
 * @returns - Base 64 image string
 */
export const generatePreviewImage = async () => {
  try {
    const canvas = await html2canvas(
      document?.getElementById("page_0") as any,
      {
        width: 800,
        height: 488,
        scale: 0.5,
      }
    );
    return canvas.toDataURL("image/webp");
  } catch (error) {
    return "";
  }
};

/**
 * @description Creates an array using conditions string.
 * @param {string} inputString - conditions string
 * @returns {Array} - Formatted array
 */
const parseRuleString = (inputString: string): Array<any> => {
  try {
    const resultArray = inputString
      .split(/\s+(AND|OR)\s+/)
      .reduce((acc: Array<any>, val, index, arr) => {
        if (index % 2 === 0) {
          // Add the delimiter to the end of the previous element if it exists
          if (index > 0) {
            acc[acc.length - 1] += ` ${arr[index - 1]}`;
          }
          acc.push(val);
        }
        return acc;
      }, []);

    return resultArray;
  } catch (e) {
    return [];
  }
};

/**
 * @description Create unique key.
 */
export const generateUniqueKey = () => {
  const timestamp = Date.now().toString(36);
  const randomStr = Math.random().toString(36).substring(2, 8);
  return `${timestamp}-${randomStr}`;
};

/**
 * @description Format rules text and returns an array.
 * @param {Array<string>} inputRules - Rules string
 * @returns {Array} - Formatted array
 */
export const generateRules = (
  inputRules: Array<any>,
  type: "template" | "section"
): Array<any> => {
  try {
    let rules: Array<any> = [];
    if (type == "template")
      inputRules = inputRules?.map((rule: string) => ({ id: -1, rule }));
    if (Array.isArray(inputRules)) {
      inputRules?.map(({ id, rule, ...rest }: { id: any; rule: string }) => {
        let conditions = parseRuleString(rule).map((condition: string) => {
          const splitOperator = condition.split(/(==|!=|<=|>=|=|AND|OR)/);
          const leftOperand = splitOperator[0] ? splitOperator[0].trim() : "";
          const operator = splitOperator[1] ? splitOperator[1].trim() : "";
          const rightOperand = splitOperator[2] ? splitOperator[2].trim() : "";
          const conjunction = splitOperator[3] ? splitOperator[3].trim() : "";

          return {
            leftOperand: leftOperand
              ? leftOperand
                  .replaceAll(RulePrefix.DEFAULT, "")
                  .replaceAll(RulePrefix.NUMERIC, "")
              : "",
            operator: operator ?? "",
            rightOperand: rightOperand.replaceAll(`"`, "") ?? "",
            conjunction: conjunction ?? "",
          };
        });
        rules.push({ key: generateUniqueKey(), id, conditions, ...rest });
      });
      return rules;
    }
    return [];
  } catch (e) {
    return [];
  }
};

/**
 * @description Get default sections by template group name
 * @param {string} templateGroup - Selected template group name
 * @returns {Array<any>} - Sections
 */
export const getSectionsByGroup = (templateGroup: string): Array<any> => {
  switch (templateGroup) {
    case TemplateGroups.CASE:
      return SECTIONS_CASE;
    case TemplateGroups.SUBCASE:
      return SECTIONS_SUBCASE;
    case TemplateGroups.CREDITOR:
      return SECTIONS_CREDITOR;
    case TemplateGroups.DEBTOR:
      return SECTIONS_DEBTOR;
    default:
      return SECTIONS_CASE;
  }
};
